import React, { useState, useEffect } from 'react';
import { Box, Button, Container, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import PasswordField from './components/PasswordField';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  // Verify token on component mount
  useEffect(() => {
    if (!token) {
      setError('No reset token provided');
      setVerifyingToken(false);
      return;
    }

    verifyResetToken(token);
  }, [token]);

  const verifyResetToken = async (resetToken: string) => {
    try {
      const response = await apiClient.get(`/auth/reset-password/${resetToken}`);
      const data = await response.json();

      if (data.valid) {
        setTokenValid(true);
        setUserEmail(data.email);
      } else {
        setError(data.error || 'Invalid or expired reset token');
        setTokenValid(false);
      }
    } catch (err) {
      setError('Failed to verify reset token');
      setTokenValid(false);
    } finally {
      setVerifyingToken(false);
    }
  };

  const handlePasswordValidation = (isValid: boolean) => {
    setIsPasswordValid(isValid);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!token) {
      setError('No reset token provided');
      return;
    }

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements and passwords match');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post(`/auth/reset-password/${token}`, {
        password,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setSuccess(data.message);

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login?message=password-reset-success');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while verifying token
  if (verifyingToken) {
    return (
      <Container maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Verifying reset token...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Show error if token is invalid
  if (!tokenValid) {
    return (
      <Container maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h4" align="center" gutterBottom>
              Invalid Reset Link
            </Typography>

            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>

            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              The password reset link is invalid or has expired. Please request a new one.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/account/forgot-password')}
              sx={{ mb: 2 }}
            >
              Request New Reset Link
            </Button>

            <Button fullWidth variant="text" onClick={() => navigate('/account/login')}>
              Back to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" align="center" gutterBottom>
            Reset Password
          </Typography>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 1 }}>
            Resetting password for:
          </Typography>
          <Typography variant="body1" align="center" fontWeight="medium" sx={{ mb: 3 }}>
            {userEmail}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <PasswordField
              onPasswordChange={setPassword}
              onValidationChange={handlePasswordValidation}
              showConfirmField={true}
              showValidation={true}
              disabled={loading}
              autoFocus={true}
              margin="normal"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !isPasswordValid}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>

          <Button
            fullWidth
            variant="text"
            onClick={() => navigate('/account/login')}
            disabled={loading}
          >
            Back to Login
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
